import {
  CheckCircle2,
  Clock3,
  Users,
} from "lucide-react";

import { AnimatedNumber } from "@/components/ui/AnimatedNumber";
import { Card } from "@/components/ui/card";

type Props = {
  totalMembers: number;
  checkedInCount: number;
  notCheckedInCount: number;
};

export function AttendanceSummaryCard({
  totalMembers,
  checkedInCount,
  notCheckedInCount,
}: Props) {
  const checkedInPercent =
    totalMembers > 0
      ? Math.round((checkedInCount / totalMembers) * 100)
      : 0;

  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <SummaryMetric
        icon={<CheckCircle2 size={23} />}
        label="Checked In"
        value={checkedInCount}
        description={`${checkedInPercent}% of active members`}
        accentClassName="from-emerald-500 via-teal-400 to-cyan-400"
        iconClassName="bg-emerald-500/15 text-emerald-400"
        valueClassName="text-emerald-400"
      />

      <SummaryMetric
        icon={<Clock3 size={23} />}
        label="Not Checked In"
        value={notCheckedInCount}
        description="Still available for check-in"
        accentClassName="from-blue-500 via-indigo-500 to-violet-400"
        iconClassName="bg-blue-500/15 text-blue-400"
        valueClassName="text-blue-400"
      />

      <SummaryMetric
        icon={<Users size={23} />}
        label="Total Members"
        value={totalMembers}
        description="Active roster members"
        accentClassName="from-slate-500 via-slate-400 to-slate-300"
        iconClassName="bg-slate-700 text-slate-300"
        valueClassName="text-white"
      />
    </div>
  );
}

function SummaryMetric({
  icon,
  label,
  value,
  description,
  accentClassName,
  iconClassName,
  valueClassName,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  description: string;
  accentClassName: string;
  iconClassName: string;
  valueClassName: string;
}) {
  return (
    <Card className="group relative overflow-hidden p-5 hover:-translate-y-2 hover:scale-[1.01]">
      <div
        className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accentClassName}`}
      />

      <div
        className={`flex h-12 w-12 items-center justify-center rounded-xl transition duration-300 group-hover:scale-110 ${iconClassName}`}
      >
        {icon}
      </div>

      <p className="mt-5 text-sm font-medium text-slate-400">
        {label}
      </p>

      <AnimatedNumber
        value={value}
        className={`mt-2 block text-4xl font-bold tracking-tight ${valueClassName}`}
      />

      <p className="mt-3 text-xs text-slate-600">
        {description}
      </p>
    </Card>
  );
}